import React, { useState, useEffect } from 'react';
import './InfoSubmissions.css'; // We'll create this file next

function InfoSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, [categoryFilter, currentPage, search]);

  const fetchSubmissions = async () => {
    const token = localStorage.getItem('adminToken');
    setLoading(true);
    try {
      const url = new URL(`${import.meta.env.VITE_API_URL}/api/admin/info-submissions`);
      url.searchParams.append('page', currentPage);
      url.searchParams.append('limit', 20);
      if (categoryFilter !== 'all') url.searchParams.append('category', categoryFilter);
      if (search) url.searchParams.append('search', search);
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setSubmissions(data.data);
        setStats(data.stats);
        setTotalPages(data.totalPages);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/info-submissions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setShowDeleteConfirm(false);
        setSelectedSubmission(null);
        fetchSubmissions();
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'startup': 'Startup',
      'msme': 'MSME',
      'mid to large': 'Mid to Large',
      'large enterprise': 'Large Enterprise'
    };
    return labels[category] || category || 'Not Specified';
  };

  const getCategoryBadgeClass = (category) => {
    const classes = {
      'startup': 'category-startup',
      'msme': 'category-msme',
      'mid to large': 'category-midlarge',
      'large enterprise': 'category-large'
    };
    return classes[category] || 'category-default';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Company', 'Email', 'Work Email', 'Phone', 'Country', 'Category', 'Message', 'Submitted At'];
    const csvData = submissions.map(s => [
      s.name,
      s.companyName,
      s.email,
      s.workEmail || 'N/A',
      s.phone || 'N/A',
      s.country || 'N/A',
      getCategoryLabel(s.businessCategory),
      s.message.replace(/,/g, ' '),
      formatDate(s.submittedAt)
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `info-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearSearch = () => {
    setSearch('');
    setCurrentPage(1);
  };

  const viewDetails = (submission) => {
    setSelectedSubmission(submission);
    setShowModal(true);
  };

  if (loading && submissions.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading submissions...</p>
      </div>
    );
  }

  return (
    <div className="info-submissions">
      <div className="admin-header">
        <h1>Information Submissions</h1>
        <button className="export-btn" onClick={exportToCSV}>
          📊 Export to CSV
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total || 0}</div>
            <div className="stat-label">Total Submissions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.today || 0}</div>
            <div className="stat-label">Today</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.thisWeek || 0}</div>
            <div className="stat-label">This Week</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.thisMonth || 0}</div>
            <div className="stat-label">This Month</div>
          </div>
        </div>
      )}

      {/* Category Breakdown Panel */}
      {stats && stats.byCategory && stats.byCategory.length > 0 && (
        <div className="type-breakdown-panel">
          <div className="panel-title">
            <h3>Submissions by Category</h3>
          </div>
          <div className="type-breakdown-body">
            <div className="type-chart">
              {stats.byCategory.map(item => (
                <div key={item._id} className="type-bar-item">
                  <div className="type-info">
                    <div className={`type-color-dot ${getCategoryBadgeClass(item._id)}`} />
                    <span className="type-name">{getCategoryLabel(item._id)}</span>
                    <span className="type-count">({item.count})</span>
                  </div>
                  <div className="bar-wrapper">
                    <div className="bar-track">
                      <div 
                        className={`bar-progress ${getCategoryBadgeClass(item._id)}`}
                        style={{ width: `${(item.count / stats.total) * 100}%` }}
                      />
                    </div>
                    <span className="bar-percent">
                      {Math.round((item.count / stats.total) * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Top Countries */}
      {stats && stats.byCountry && stats.byCountry.length > 0 && (
        <div className="type-breakdown-panel">
          <div className="panel-title">
            <h3>Top Countries</h3>
          </div>
          <div className="type-breakdown-body">
            <div className="top-countries-list">
              {stats.byCountry.map((country, index) => (
                <div key={country._id} className="country-item">
                  <span className="country-rank">{index + 1}.</span>
                  <span className="country-name">{country._id || 'Not specified'}</span>
                  <span className="country-count">{country.count} submissions</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Unified Filter + Search Strip */}
      <div className="filter-search-strip">
        <div className="filter-section">
          <span className="filter-label">Filter by category:</span>
          <div className="filter-chips">
            <button 
              className={`filter-chip ${categoryFilter === 'all' ? 'active' : ''}`}
              onClick={() => setCategoryFilter('all')}
            >
              All
            </button>
            <button 
              className={`filter-chip ${categoryFilter === 'startup' ? 'active' : ''}`}
              onClick={() => setCategoryFilter('startup')}
            >
              Startup
            </button>
            <button 
              className={`filter-chip ${categoryFilter === 'msme' ? 'active' : ''}`}
              onClick={() => setCategoryFilter('msme')}
            >
              MSME
            </button>
            <button 
              className={`filter-chip ${categoryFilter === 'mid to large' ? 'active' : ''}`}
              onClick={() => setCategoryFilter('mid to large')}
            >
              Mid to Large
            </button>
            <button 
              className={`filter-chip ${categoryFilter === 'large enterprise' ? 'active' : ''}`}
              onClick={() => setCategoryFilter('large enterprise')}
            >
              Large Enterprise
            </button>
          </div>
        </div>
        
        <div className="filter-divider"></div>
        
        <div className="search-section">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by name, email, company, or message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="clear-search" onClick={clearSearch}>
                ✕
              </button>
            )}
            <button className="refresh-btn" onClick={fetchSubmissions}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Company</th>
              <th>Email</th>
              <th>Work Email</th>
              <th>Phone</th>
              <th>Country</th>
              <th>Category</th>
              <th>Submitted At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {submissions.map((submission) => (
              <tr key={submission._id}>
                <td>{submission.name}</td>
                <td>{submission.companyName}</td>
                <td>{submission.email}</td>
                <td>{submission.workEmail || 'N/A'}</td>
                <td>{submission.phone || 'N/A'}</td>
                <td>{submission.country || 'N/A'}</td>
                <td>
                  <span className={`type-badge ${getCategoryBadgeClass(submission.businessCategory)}`}>
                    {getCategoryLabel(submission.businessCategory)}
                  </span>
                </td>
                <td>{formatDate(submission.submittedAt)}</td>
                <td className="action-buttons">
                  <button 
                    className="view-btn"
                    onClick={() => viewDetails(submission)}
                    title="View Details"
                  >
                    👁️ View
                  </button>
                  <button 
                    className="delete-btn"
                    onClick={() => {
                      setSelectedSubmission(submission);
                      setShowDeleteConfirm(true);
                    }}
                    title="Delete"
                  >
                    🗑️ Delete
                  </button>
                </td>
              </tr>
            ))}
            {submissions.length === 0 && (
              <tr>
                <td colSpan="9" className="no-data">
                  No submissions found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="page-btn"
          >
            ← Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages} ({total} total)
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            Next →
          </button>
        </div>
      )}

      {/* View Details Modal */}
      {showModal && selectedSubmission && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Submission Details</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="details-grid">
                <div className="detail-group">
                  <label>Name:</label>
                  <p>{selectedSubmission.name}</p>
                </div>
                <div className="detail-group">
                  <label>Company:</label>
                  <p>{selectedSubmission.companyName}</p>
                </div>
                <div className="detail-group">
                  <label>Email:</label>
                  <p>{selectedSubmission.email}</p>
                </div>
                <div className="detail-group">
                  <label>Work Email:</label>
                  <p>{selectedSubmission.workEmail || 'N/A'}</p>
                </div>
                <div className="detail-group">
                  <label>Phone:</label>
                  <p>{selectedSubmission.phone || 'N/A'}</p>
                </div>
                <div className="detail-group">
                  <label>Country:</label>
                  <p>{selectedSubmission.country || 'N/A'}</p>
                </div>
                <div className="detail-group">
                  <label>Business Category:</label>
                  <p>
                    <span className={`type-badge ${getCategoryBadgeClass(selectedSubmission.businessCategory)}`}>
                      {getCategoryLabel(selectedSubmission.businessCategory)}
                    </span>
                  </p>
                </div>
                <div className="detail-group">
                  <label>Submitted At:</label>
                  <p>{formatDate(selectedSubmission.submittedAt)}</p>
                </div>
                <div className="detail-group full-width">
                  <label>Message:</label>
                  <div className="message-box">
                    {selectedSubmission.message}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="close-modal-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedSubmission && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="modal-close" onClick={() => setShowDeleteConfirm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the submission from <strong>{selectedSubmission.name}</strong>?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="confirm-delete-btn" onClick={() => handleDelete(selectedSubmission._id)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InfoSubmissions;